import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import DynamicIcon from "../DynamicIcon";
import AddPoolElement from "../templates/AddPoolElement";
import CustomSelectElement from "../CustomSelectElement";
import { selectAccountData } from "../../redux/main/accountSlice";
import { useColorPalette } from '../../hooks/useColorPalette';
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns';

const initialState = {
    typeId: "",
    typeLabel: "",
    testedDate: "",
    expirationWarrantyDate: ""
}

// Questo componente (DA MODIFICARE PER FARE IN MODO CHE: ), ricevendo systemId,
// genera, modifica e cancella elementi dell'array typesList di un system sfruttando Redux
const CustomerServicesPool = forwardRef(({ arrayData, addEditService, deleteService }, ref) => {
    // Usa la palette colori dinamica per uniformarsi al resto dell'applicazione
    const { palette = {} } = useColorPalette() || {};
    // Recupero i dati dell'account (inclusi i tipi di impianto)
    const accountData = useSelector(selectAccountData);
    
    // Funzione per verificare lo stato della garanzia
    const checkWarrantyStatus = (expirationDate) => {
        if (!expirationDate) return null;
        
        const now = new Date();
        const expDate = new Date(expirationDate);
        
        if (isNaN(expDate.getTime())) return null;
        
        return expDate > now;
    };

    // Gestisco l'apertura e chiusura del form
    // Questo stato passerà ai figli
    const [isAdding, setIsAdding] = useState(false);
    // Gestisco gli input del form
    const [formInput, setFormInput] = useState(initialState);
    const handleFormInput = (event) => {
        const { id, value } = event?.target;
        setFormInput(prevState => ({
            ...prevState,
            [id]: value
        }))
    }
    // Gestisco la modalità edit
    const editInitialState = {
        isEditMode: false,
        index: null
    }
    const [edit, setEdit] = useState(editInitialState)
    // Gestisco il settaggio di edit
    const handleSetEdit = (index) => {
        setEdit({
            isEditMode: true,
            index
        });
        setIsAdding(true);
    };
    // Se edit è impostato, imposto i valori dell'oggetto all'indice specificato in formInput
    useEffect(() => {
        if (edit.isEditMode && edit.index !== null) {
            setFormInput(arrayData[edit.index])
        }
    }, [edit]);
    // Gestisco l'annullamento della modalità edit (tornando alla modalità add e chiudendo il dropdown)
    const handleCancelEdit = () => {
        setEdit(editInitialState);
        setFormInput(initialState);
        setIsAdding(false);
    }
    // Se chiudo la modifica con lo switch, azzero formInput ed esco dalla modalità edit
    useEffect(() => {
        if (!isAdding) {
            setEdit(editInitialState);
            setFormInput(initialState);
        } else if (!edit.isEditMode) {
            // Se stiamo aprendo il form per aggiungere un nuovo tipo (non in modalità edit)
            // Seleziona automaticamente il primo tipo e attiva il form
            if (accountData?.settings?.systemTypes && accountData.settings.systemTypes.length > 0) {
                const firstType = accountData.settings.systemTypes[0];
                // Date predefinite
                const today = new Date().toISOString().split('T')[0];
                const twoYearsFromNow = new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toISOString().split('T')[0];
                
                // Imposta automaticamente il primo tipo e le date predefinite
                setFormInput({
                    typeId: firstType._id,
                    typeLabel: firstType.label,
                    testedDate: today,
                    expirationWarrantyDate: twoYearsFromNow
                });
                console.log('Form inizializzato con il primo tipo disponibile:', firstType.label);
            }
        }
    }, [isAdding, accountData?.settings?.systemTypes])

    // Controllo che siano inseriti almeno typeId e typeLabel
    const [isAllField, setIsAllField] = useState(false);
    const checkAllField = () => {
        const requiredFields = ['typeId', 'typeLabel'];
        const allRequiredFieldsValid = requiredFields.every(field => {
            const value = (formInput)[field];
            return value && value.trim() !== '';
        });
        setIsAllField(allRequiredFieldsValid);
    }
    useEffect(() => {
        checkAllField();
    }, [formInput])

    // Gestisco il tasto aggiungi, il quale deve anche chiudere la tendina di aggiunta indirizzo
    const handleFormSubmit = () => {
        if (edit.isEditMode && edit.index !== null) {
            addEditService(formInput, edit.index);
            setFormInput(initialState);
            setIsAdding(false);

        } else {
            addEditService(formInput);
            setFormInput(initialState);
            setIsAdding(false);
        }
    }

    // Funzione per gestire i tipi di impianto
    // accountData è già definito all'inizio del componente
    const handleServiceType = (value, label) => {
        // Cerco il tipo di impianto selezionato nell'elenco completo per ottenere i dati aggiuntivi
        const selectedType = accountData?.settings?.systemTypes?.find(type => type._id === value);
        
        // Log per debug
        console.log('Tipo impianto selezionato:', selectedType);
        
        // Funzione per formattare correttamente le date nel formato YYYY-MM-DD
        const formatDateString = (dateValue) => {
            // Se la data è già in formato stringa, ma è in formato ISO completo, estraiamo solo la parte della data
            if (typeof dateValue === 'string' && dateValue.includes('T')) {
                return dateValue.split('T')[0];
            }
            
            try {
                // Altrimenti, creiamo un nuovo oggetto Date e lo formattiamo
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) return ''; // Gestisce date non valide
                
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0'); // I mesi sono 0-indexed
                const day = String(date.getDate()).padStart(2, '0');
                
                return `${year}-${month}-${day}`;
            } catch (error) {
                console.error('Errore nella formattazione della data:', error);
                return '';
            }
        };
        
        // Date predefinite
        const today = formatDateString(new Date());
        const twoYearsFromNow = formatDateString(new Date(new Date().setFullYear(new Date().getFullYear() + 2)));
        
        // Estrai e formatta le date dal tipo selezionato
        const testedDateValue = selectedType?.testedDate ? formatDateString(selectedType.testedDate) : today;
        const warrantyDateValue = selectedType?.expirationWarrantyDate ? formatDateString(selectedType.expirationWarrantyDate) : twoYearsFromNow;
        
        console.log('Date formattate:', {
            testedDate: testedDateValue,
            expirationWarrantyDate: warrantyDateValue
        });
        
        setFormInput((prevState) => ({
            ...prevState,
            typeId: value,
            typeLabel: label,
            testedDate: prevState.testedDate || testedDateValue,
            expirationWarrantyDate: prevState.expirationWarrantyDate || warrantyDateValue
        }));
        
        // Log per debug dei valori impostati
        console.log('Form input aggiornato dopo selezione tipo:', formInput);
    }
    // Espone la funzione setIsAdding tramite ref
    useImperativeHandle(ref, () => ({
        setIsAdding
    }));
    
    return (
        <div className="w-full flex flex-col gap-2 p-4 bg-white rounded-md shadow-sm">
            {/* Intestazione rimossa poiché il pulsante è stato spostato nel componente padre */}
            
            {/* FORM DI AGGIUNTA/MODIFICA */}
            {isAdding && (
                <div className="relative px-4 mb-6">
                    <AddPoolElement title={edit.isEditMode && edit.index !== null ? 'MODIFICA TIPO IMPIANTO' : 'AGGIUNGI TIPO IMPIANTO'} isAdding={isAdding} setIsAdding={setIsAdding}>
                    <div className="mt-4 flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            {/* Tipo di servizio */}
                            <div className="input-box">
                                <label htmlFor="typesList">Tipo di impianto</label>
                                <CustomSelectElement
                                    id="typesList"
                                    name="typesList"
                                    list={accountData?.settings.systemTypes}
                                    setter={handleServiceType}
                                    defaultValue={formInput.typeId || (accountData?.settings.systemTypes && accountData?.settings.systemTypes.length > 0 ? accountData?.settings.systemTypes[0]._id : '')}
                                    valueKey="_id"
                                    labelKey="label"
                                />
                            </div>
                            <div className="flex gap-2">
                                <div className="input-box flex-1">
                                <label htmlFor="testedDate">Data collaudo</label>
                                <input
                                    type="date"
                                    id="testedDate"
                                    onChange={handleFormInput} 
                                    value={typeof formInput.testedDate === 'string' && formInput.testedDate.includes('T') 
                                        ? formInput.testedDate.split('T')[0] 
                                        : formInput.testedDate || ""}
                                    className="max-w-48 border p-2 rounded-lg outline-slate-500"
                                />
                            </div>
                            <div className="input-box flex-1">
                                <label htmlFor="expirationWarrantyDate">Data scadenza garantita</label>
                                <input
                                    type="date"
                                    id="expirationWarrantyDate"
                                    onChange={handleFormInput}
                                    value={typeof formInput.expirationWarrantyDate === 'string' && formInput.expirationWarrantyDate.includes('T') 
                                        ? formInput.expirationWarrantyDate.split('T')[0] 
                                        : formInput.expirationWarrantyDate || ""}
                                    className="max-w-48 border p-2 rounded-lg outline-slate-500"
                                />
                            </div>
                            </div>
                            
                        </div>
                        <div className="w-full flex justify-end">
                            {
                                isAllField ? (
                                    <div className="flex gap-2">
                                        {edit.isEditMode && (
                                            <button 
                                                className="flex items-center gap-2 px-4 py-2 rounded" 
                                                onClick={handleCancelEdit}
                                                style={{
                                                    backgroundColor: palette?.buttonSecondaryBg || '#6c757d',
                                                    color: palette?.buttonSecondaryText || '#ffffff',
                                                    border: `1px solid ${palette?.buttons?.secondary?.border || 'transparent'}`
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.backgroundColor = palette?.buttonSecondaryHover || '#5a6268';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.backgroundColor = palette?.buttonSecondaryBg || '#6c757d';
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-times mr-1" style={{ color: palette?.buttonSecondaryText || '#ffffff' }} />
                                                Annulla
                                            </button>
                                        )}
                                        <button 
                                            className="flex items-center gap-2 px-4 py-2 rounded" 
                                            onClick={handleFormSubmit}
                                            style={{
                                                backgroundColor: palette?.buttonPrimaryBg || palette?.primary || '#0078d4',
                                                color: palette?.buttonPrimaryText || palette?.textOnPrimary || '#ffffff',
                                                border: `1px solid ${palette?.buttons?.primary?.border || 'transparent'}`
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = palette?.buttonPrimaryHover || palette?.primaryDark || '#006dbf';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor = palette?.buttonPrimaryBg || palette?.primary || '#0078d4';
                                            }}
                                        >
                                            <DynamicIcon 
                                                iconClass={edit?.isEditMode ? "fa-solid fa-check mr-1" : "fa-solid fa-plus mr-1"} 
                                                style={{ color: palette?.buttonPrimaryText || palette?.textOnPrimary || '#ffffff' }} 
                                            />
                                            {edit?.isEditMode ? 'Aggiorna' : 'Aggiungi'}
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        className="flex items-center gap-2 px-4 py-2 rounded cursor-not-allowed opacity-70" 
                                        disabled
                                        style={{
                                            backgroundColor: (palette?.buttonDisabledBg || palette?.formBg || '#e9ecef'),
                                            color: (palette?.buttonDisabledText || palette?.textMuted || '#6c757d'),
                                            border: `1px solid ${(palette?.buttons?.disabled?.border || palette?.formBorderColor || '#ced4da')}`
                                        }}
                                    >
                                        <DynamicIcon 
                                            iconClass={edit?.isEditMode ? "fa-solid fa-check mr-1" : "fa-solid fa-plus mr-1"} 
                                            style={{ color: palette?.buttonDisabledText || palette?.textMuted || '#6c757d' }} 
                                        />
                                        {edit?.isEditMode ? 'Aggiorna' : 'Aggiungi'}
                                    </button>
                                )
                            }
                        </div>
                    </div>
                </AddPoolElement>
                </div>
            )}
            
            {/* TABELLA DI RENDERING ELEMENTI ARRAY */}
            <div className="px-4">
                <h3 className="text-lg font-semibold mb-4">Elenco tipologie impianto</h3>
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="h-10 border-b border-slate-300">
                            <th className="w-12 text-left pl-2">#</th>
                            <th className="text-left pl-2">IMPIANTO</th>
                            <th className="text-left pl-2">DATA COLLAUDO</th>
                            <th className="text-left pl-2">SCADENZA GARANZIA</th>
                            <th className="text-center w-[120px]">MODIFICA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            arrayData?.map((row, index) => {
                                return (
                                    <React.Fragment key={index}>
                                        <tr className="h-12 border-b border-slate-300 hover:bg-slate-50">
                                            <td className="w-12 pl-2">{index + 1}</td>
                                            <td className="pl-2">{row.typeLabel}</td>
                                            <td className="pl-2">{row.testedDate ? (() => {
                                                try {
                                                    return format(new Date(row.testedDate), "dd/MM/yyyy");
                                                } catch (error) {
                                                    console.error("Errore formattando testedDate:", error);
                                                    return row.testedDate;
                                                }
                                            })() : ""}</td>
                                            <td className="pl-2">
                                                <div className="flex items-center gap-2">
                                                    {row.expirationWarrantyDate ? (() => {
                                                        try {
                                                            return (
                                                                <>
                                                                    <span>{format(new Date(row.expirationWarrantyDate), "dd/MM/yyyy")}</span>
                                                                    {checkWarrantyStatus(row.expirationWarrantyDate) !== null && (
                                                                        <span 
                                                                            className={`flex items-center justify-center rounded-full w-5 h-5 text-white`}
                                                                            title={checkWarrantyStatus(row.expirationWarrantyDate) ? 'In garanzia' : 'Garanzia scaduta'}
                                                                            style={{
                                                                                backgroundColor: checkWarrantyStatus(row.expirationWarrantyDate) ? 
                                                                                    (palette?.success || '#22c55e') : 
                                                                                    (palette?.danger || '#ef4444')
                                                                            }}
                                                                        >
                                                                            {checkWarrantyStatus(row.expirationWarrantyDate) ? 
                                                                                <i className="fas fa-check text-xs"></i> : 
                                                                                <i className="fas fa-exclamation-triangle text-xs"></i>}
                                                                        </span>
                                                                    )}
                                                                </>
                                                            );
                                                        } catch (error) {
                                                            console.error("Errore formattando expirationWarrantyDate:", error);
                                                            return row.expirationWarrantyDate;
                                                        }
                                                    })() : ""}
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="flex justify-center items-center gap-4">
                                                    <button 
                                                        className="text-gray-600 hover:text-gray-800" 
                                                        onClick={() => handleSetEdit(index)}
                                                        style={{ color: palette?.textMuted || '#6b7280' }}
                                                    >
                                                        <DynamicIcon iconClass="fa-solid fa-pen-to-square" />
                                                    </button>
                                                    <button 
                                                        className="text-gray-600 hover:text-gray-800" 
                                                        onClick={() => deleteService(index)}
                                                        style={{ color: palette?.textMuted || '#6b7280' }}
                                                    >
                                                        <DynamicIcon iconClass="fa-solid fa-trash" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                )
                            })
                        }
                    </tbody>
                </table>
            </div>


        </div >
    )
});

export default CustomerServicesPool;
